/**
 * ToolResultErrorBoundary — wraps a single tool-result renderer in an
 * error boundary so a buggy component doesn't blow up the whole chat
 * panel. PHASE_E §4.1 emits `component_render_error` telemetry when one
 * trips.
 *
 * Class component because React's error-boundary contract requires
 * componentDidCatch / getDerivedStateFromError — function components
 * cannot catch render errors. Rendered fallback is intentionally minimal
 * (the agent's prose typically carries the meaning anyway).
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  toolName: string;
  /** Fired once when an error is caught. Wired to useChatTelemetry's
   * logEvent in the parent so the hook stays in the function-component
   * world and this class component stays dumb. */
  onError: (toolName: string, message: string) => void;
  children: ReactNode;
}

interface State {
  errored: boolean;
}

export class ToolResultErrorBoundary extends Component<Props, State> {
  state: State = { errored: false };

  static getDerivedStateFromError(): State {
    return { errored: true };
  }

  componentDidCatch(error: Error, _info: ErrorInfo) {
    this.props.onError(this.props.toolName, error.message || 'unknown render error');
  }

  render() {
    if (this.state.errored) {
      return (
        <div className="mt-2 border border-error/40 bg-error/5 px-3 py-2 text-body-sm text-txt-secondary">
          A renderer for{' '}
          <span className="font-mono text-caption">{this.props.toolName}</span>{' '}
          failed. The agent's prose above carries the answer.
        </div>
      );
    }
    return this.props.children;
  }
}
